/**
 * Agent utilities - Shared logic for agent initialization
 */

import { McpClient } from '../mcp/client.js';
import { isAuthenticated } from '../utils/config.js';
import { muted, warning } from '../ui/theme/index.js';
import { getMcpTools, executeMcpTool } from '../mcp/tools.js';
import type { McpState, McpToolResult, McpToolInfo } from '../agent/types.js';

export interface AgentInitOptions {
    mcp?: boolean;
    debug?: boolean;
}

export async function initializeAgent(options: AgentInitOptions): Promise<{
    mcpState?: McpState;
    onMcpToolCall: (callId: string, serverName: string, toolName: string, args: Record<string, unknown>) => Promise<McpToolResult>;
    mcpClient?: McpClient;
}> {
    const { mcp: enableMcp, debug } = options;

    let mcpState: McpState | undefined;
    let mcpClient: McpClient | undefined;

    // Start with built-in paean-system tools
    const builtInTools: McpToolInfo[] = getMcpTools().map(tool => ({
        name: tool.name,
        description: tool.description || '',
        inputSchema: tool.inputSchema as Record<string, unknown>,
    }));

    const mcpServers: McpState['mcpServers'] = [{
        name: 'paean-system',
        connected: true,
        tools: builtInTools,
    }];

    // Track which servers need lazy connection
    const pendingServers = new Set<string>();

    if (enableMcp) {
        mcpClient = new McpClient({ debug });
        const serverNames = mcpClient.listServers();

        if (serverNames.length > 0) {
            if (debug) {
                console.log(muted(`[MCP] Found ${serverNames.length} configured server(s) (lazy connect)`));
            }

            // Register servers as pending - they'll connect on first use
            for (const name of serverNames) {
                pendingServers.add(name);
                // Add server entry with empty tools - will be populated on connect
                const configTools = mcpClient.getServerConfigTools(name);
                mcpServers.push({
                    name,
                    connected: false,
                    tools: configTools || [],
                });
            }
        }
    }

    mcpState = {
        mcpEnabled: true,
        mcpServers,
    };

    /**
     * Lazy-connect to an MCP server on first tool call
     */
    const ensureConnected = async (serverName: string): Promise<boolean> => {
        if (!mcpClient || !pendingServers.has(serverName)) return true;

        try {
            if (debug) {
                console.log(muted(`[MCP] Connecting to ${serverName}...`));
            }
            const tools = await mcpClient.connect(serverName);
            pendingServers.delete(serverName);

            // Update mcpServers entry
            const serverEntry = mcpServers.find(s => s.name === serverName);
            if (serverEntry) {
                serverEntry.connected = true;
                serverEntry.tools = Array.from(mcpClient.getAllTools().get(serverName) || []);
            }

            if (debug) {
                console.log(muted(`[MCP] Connected to ${serverName}: ${tools.length} tools`));
            }
            return true;
        } catch (error) {
            pendingServers.delete(serverName);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            console.log(
                warning(`Failed to connect to MCP server "${serverName}": ${errorMessage}`)
            );
            return false;
        }
    };

    const onMcpToolCall = async (
        _callId: string,
        serverName: string,
        toolName: string,
        args: Record<string, unknown>
    ): Promise<McpToolResult> => {
        // Handle built-in paean-system tools
        if (serverName === 'paean-system') {
            try {
                const result = await executeMcpTool(toolName, args);
                return {
                    content: [{ type: 'text', text: JSON.stringify(result, null, 2) }],
                    isError: false,
                };
            } catch (error) {
                return {
                    content: [{ type: 'text', text: `Error: ${(error as Error).message}` }],
                    isError: true,
                };
            }
        }

        // Handle external MCP tools - lazy connect if needed
        if (!mcpClient) {
            return {
                content: [{ type: 'text', text: 'MCP client not available' }],
                isError: true,
            };
        }

        // Lazy connect on first call
        await ensureConnected(serverName);

        return mcpClient.callTool(serverName, toolName, args);
    };

    return { mcpState, onMcpToolCall, mcpClient };
}

export function checkAuthentication(): void {
    if (!isAuthenticated()) {
        console.log(warning('⚠️  Not logged in. Run `paean login` first.\n'));
        process.exit(1);
    }
}